/**
 * Política Corporativa de Viagens (F02), cadastro de Dados Mestres mantido por Facilities: versões,
 * conteúdo editável e o motor que aplica a política à viagem e às despesas. O motor é puro: recebe
 * a versão (o retrato gravado na viagem) e os valores já na moeda do limite, e devolve o resultado
 * de cada regra: dentro da política, exceção (justificativa e aprovação pela alçada) ou não
 * permitido. Os valores vêm sempre da versão, nunca do código.
 */
import { z } from 'zod';
import { businessDaysBetween } from './approvals.ts';
import { localizedText } from './schemas.ts';

export const TRAVEL_POLICY_STATUSES = [
  'draft',
  'approved',
  'active',
  'superseded',
  'inactive',
] as const;
export type TravelPolicyStatus = (typeof TRAVEL_POLICY_STATUSES)[number];

export const TRAVEL_LIMIT_KINDS = ['lodging', 'meal', 'advance', 'minibar'] as const;
export type TravelLimitKind = (typeof TRAVEL_LIMIT_KINDS)[number];
export const TRAVEL_MEALS = ['breakfast', 'lunch', 'dinner', 'daily'] as const;
export type TravelMeal = (typeof TRAVEL_MEALS)[number];
export const TRAVEL_LIMIT_UNITS = ['per_day', 'per_meal', 'per_trip', 'per_event'] as const;
export const TRAVEL_SCOPES_POLICY = ['domestic', 'international'] as const;
export type TravelPolicyScope = (typeof TRAVEL_SCOPES_POLICY)[number];
export const TRAVEL_TREATMENTS = ['allowed', 'exception', 'forbidden'] as const;
export type TravelTreatment = (typeof TRAVEL_TREATMENTS)[number];
export const TRAVEL_TRANSPORT_CODES = [
  'air',
  'bus',
  'car_rental',
  'own_car',
  'app',
  'taxi',
  'public',
  'upgrade',
  'checked_baggage',
] as const;
export type TravelTransportCode = (typeof TRAVEL_TRANSPORT_CODES)[number];

/** Prazos e referências da política (o valor é da versão; a unidade é fixa). */
export const TRAVEL_PARAMETERS = {
  request_min_days: 'days',
  air_preference_km: 'km',
  advance_min_business_days: 'business_days',
  accountability_days: 'days',
  reimbursement_days: 'days',
  passport_min_days: 'days',
  laundry_min_days: 'days',
  rest_period_hours: 'hours',
} as const;
export type TravelParameterCode = keyof typeof TRAVEL_PARAMETERS;
export const TRAVEL_PARAMETER_CODES = Object.keys(TRAVEL_PARAMETERS) as TravelParameterCode[];

// ----- Entradas da tela -----

const code = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_]{0,29}$/, 'Use letras, números ou _ (até 30)');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const optionalDate = isoDate
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const amount = z
  .string()
  .trim()
  .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais');
const currency = z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida');
const country = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, 'Use o código do país (2 letras)');
const emptyText = { 'pt-BR': '', en: '' };
const optionalText = z
  .object({ 'pt-BR': z.string().trim().max(2000), en: z.string().trim().max(2000) })
  .default(emptyText);

/** Nova versão: do zero (vazia) ou copiando outra (inclusive a demonstrativa). */
export const travelPolicyCreateInput = z.object({
  description: z.string().trim().min(3).max(500),
  legalEntityId: z.uuid().nullable().default(null),
  copyFromId: z.uuid().nullable().default(null),
});
export const travelPolicyVersionInput = z.object({
  description: z.string().trim().min(3).max(500),
  validFrom: optionalDate,
  validUntil: optionalDate,
});
export const travelPolicyStatusInput = z
  .object({
    status: z.enum(['approved', 'active', 'draft', 'inactive']),
    validFrom: optionalDate,
    reason: z.string().trim().min(3).max(1000).nullable().optional(),
  })
  .refine((v) => !['draft', 'inactive'].includes(v.status) || !!v.reason, {
    path: ['reason'],
    message: 'Explique o motivo',
  })
  .refine((v) => v.status !== 'active' || !!v.validFrom, {
    path: ['validFrom'],
    message: 'Informe o início da vigência',
  });

export const travelPolicyCategoryInput = z.object({
  code,
  name: localizedText,
  minAdvanceDays: z.number().int().min(0).max(365).nullable().default(null),
  requiredDocuments: optionalText,
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(1).max(999).default(1),
});
export const travelPolicyBandInput = z.object({
  code,
  name: localizedText,
  isDefault: z.boolean().default(false),
  sortOrder: z.number().int().min(1).max(999).default(1),
  /** Níveis do catálogo de cargos enquadrados na faixa (Dados Mestres, sem outro catálogo). */
  jobLevelIds: z.array(z.uuid()).max(200).default([]),
});
export const travelPolicyRegionInput = z.object({
  code,
  name: localizedText,
  countries: z.array(country).max(250).default([]),
  sortOrder: z.number().int().min(1).max(999).default(1),
});
export const travelPolicyLimitInput = z
  .object({
    kind: z.enum(TRAVEL_LIMIT_KINDS),
    meal: z.enum(TRAVEL_MEALS).nullable().default(null),
    scope: z.enum(TRAVEL_SCOPES_POLICY),
    regionId: z.uuid().nullable().default(null),
    country: country.nullable().default(null),
    city: z.string().trim().min(1).max(120).nullable().default(null),
    bandId: z.uuid().nullable().default(null),
    amount,
    currency,
    unit: z.enum(TRAVEL_LIMIT_UNITS),
    priority: z.number().int().min(-99).max(99).default(0),
    notes: z.string().trim().min(1).max(500).nullable().default(null),
  })
  .refine((v) => (v.kind === 'meal') === !!v.meal, {
    path: ['meal'],
    message: 'Refeição só no limite de alimentação (e obrigatória nele)',
  })
  .refine((v) => !v.city || !!v.country, { path: ['country'], message: 'Informe o país da cidade' })
  .refine((v) => v.scope === 'international' || !v.regionId, {
    path: ['regionId'],
    message: 'Região só na viagem internacional',
  });
export const travelPolicyParametersInput = z.object({
  values: z.partialRecord(z.enum(TRAVEL_PARAMETER_CODES), z.number().min(0).max(9999)),
});
export const travelPolicyExpenseTypeInput = z
  .object({
    code,
    name: localizedText,
    treatment: z.enum(TRAVEL_TREATMENTS),
    limitKind: z.enum(['lodging', 'meal', 'minibar']).nullable().default(null),
    requiresReceipt: z.boolean().default(true),
    requiresPriorAuthorization: z.boolean().default(false),
    isRepresentation: z.boolean().default(false),
    minTripDaysParameter: z.enum(['laundry_min_days']).nullable().default(null),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(1).max(999).default(1),
  })
  .refine((v) => v.treatment !== 'forbidden' || !v.limitKind, {
    path: ['limitKind'],
    message: 'Despesa proibida não tem limite',
  });
export const travelPolicyTransportInput = z.object({
  code: z.enum(TRAVEL_TRANSPORT_CODES),
  treatment: z.enum(TRAVEL_TREATMENTS),
  /** Só estas faixas (vazio = todas). */
  eligibleBandIds: z.array(z.uuid()).max(50).nullable().default(null),
  notes: optionalText,
});

// ----- Retrato da versão (gravado na viagem) -----

export interface TravelPolicySnapshot {
  versionId: string;
  version: number;
  isDemo: boolean;
  categories: { id: string; code: string; minAdvanceDays: number | null; isActive: boolean }[];
  bands: { id: string; code: string; isDefault: boolean; jobLevelIds: string[] }[];
  regions: { id: string; code: string; countries: string[] }[];
  limits: {
    id: string;
    kind: TravelLimitKind;
    meal: TravelMeal | null;
    scope: TravelPolicyScope;
    regionId: string | null;
    country: string | null;
    city: string | null;
    bandId: string | null;
    amount: string;
    currency: string;
    unit: string;
    priority: number;
  }[];
  parameters: Partial<Record<TravelParameterCode, number>>;
  expenseTypes: {
    code: string;
    treatment: TravelTreatment;
    limitKind: 'lodging' | 'meal' | 'minibar' | null;
    requiresReceipt: boolean;
    requiresPriorAuthorization: boolean;
    isRepresentation: boolean;
    minTripDaysParameter: TravelParameterCode | null;
    isActive: boolean;
  }[];
  transports: {
    code: TravelTransportCode;
    treatment: TravelTreatment;
    eligibleBandIds: string[] | null;
  }[];
}

export type TravelOutcome = 'within' | 'exception' | 'blocked';
/** Resultado de uma regra: o código (rótulo na tela) e os valores que a explicam. */
export interface TravelFinding {
  rule: string;
  outcome: Exclude<TravelOutcome, 'within'>;
  params?: Record<string, string | number>;
}

const DAY_MS = 86_400_000;
const days = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
const addDays = (date: string, n: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

/** O pior resultado entre as regras. */
export function overallOutcome(findings: readonly TravelFinding[]): TravelOutcome {
  if (findings.some((f) => f.outcome === 'blocked')) return 'blocked';
  if (findings.length) return 'exception';
  return 'within';
}

/** Faixa funcional do nível do cargo (sem nível enquadrado, a faixa padrão). */
export function travelBandFor(policy: TravelPolicySnapshot, jobLevelId: string | null) {
  return (
    (jobLevelId && policy.bands.find((b) => b.jobLevelIds.includes(jobLevelId))) ||
    policy.bands.find((b) => b.isDefault) ||
    null
  );
}

/** Região do país de destino. */
export function travelRegionFor(policy: TravelPolicySnapshot, country: string | null) {
  return (country && policy.regions.find((r) => r.countries.includes(country))) || null;
}

/**
 * O limite que vale para a situação. Precedência: cidade > país > região > geral do âmbito; a
 * faixa específica antes da geral; empate, a maior prioridade.
 */
export function travelLimitFor(
  policy: TravelPolicySnapshot,
  q: {
    kind: TravelLimitKind;
    meal?: TravelMeal | null;
    scope: TravelPolicyScope;
    country?: string | null;
    city?: string | null;
    bandId?: string | null;
  },
) {
  const region = q.scope === 'international' ? travelRegionFor(policy, q.country ?? null) : null;
  const city = q.city?.trim().toLowerCase() ?? null;
  const candidates = policy.limits.filter(
    (l) =>
      l.kind === q.kind &&
      l.scope === q.scope &&
      (l.meal ?? null) === (q.meal ?? null) &&
      (!l.bandId || l.bandId === q.bandId) &&
      (!l.city || (!!city && l.city.trim().toLowerCase() === city && l.country === q.country)) &&
      (!l.country || l.country === q.country) &&
      (!l.regionId || l.regionId === region?.id),
  );
  const rank = (l: (typeof candidates)[number]) =>
    (l.city ? 3000 : l.country ? 2000 : l.regionId ? 1000 : 0) + (l.bandId ? 100 : 0) + l.priority;
  return candidates.sort((a, b) => rank(b) - rank(a))[0] ?? null;
}

const toCents = (v: string) => Math.round(Number(v) * 10000);
const fromCents = (v: number) => (v / 10000).toFixed(4);

export interface TravelTripFacts {
  scope: TravelPolicyScope;
  categoryCode: string | null;
  /** Data do pedido (AAAA-MM-DD). */
  requestedOn: string;
  departureOn: string;
  returnOn: string;
  country: string | null;
  city: string | null;
  bandId: string | null;
  transports: readonly TravelTransportCode[];
  distanceKm: number | null;
  /** Diária estimada do hotel, já na moeda do limite (o servidor converte pela cotação). */
  lodgingNightly: { amount: string; currency: string } | null;
  passportValidUntil: string | null;
  /** Viagem marcada em período de descanso (domingo, feriado, intervalo entre jornadas). */
  restPeriodConflict: boolean;
}

/** Regras da viagem: antecedência, transporte, locação, hospedagem, passaporte e descanso. */
export function evaluateTravel(policy: TravelPolicySnapshot, t: TravelTripFacts): TravelFinding[] {
  const f: TravelFinding[] = [];
  const p = policy.parameters;
  const category = policy.categories.find((c) => c.code === t.categoryCode && c.isActive);
  if (!category) f.push({ rule: 'category_required', outcome: 'blocked' });
  const minDays = category?.minAdvanceDays ?? p.request_min_days ?? null;
  if (minDays !== null && days(t.requestedOn, t.departureOn) < minDays) {
    f.push({
      rule: 'late_request',
      outcome: 'exception',
      params: { minDays, days: days(t.requestedOn, t.departureOn) },
    });
  }
  for (const code of t.transports) {
    const rule = policy.transports.find((x) => x.code === code);
    if (!rule) continue;
    if (rule.treatment === 'forbidden') {
      f.push({ rule: 'transport_forbidden', outcome: 'blocked', params: { transport: code } });
    } else if (rule.treatment === 'exception') {
      f.push({ rule: 'transport_exception', outcome: 'exception', params: { transport: code } });
    }
    if (rule.eligibleBandIds?.length && !(t.bandId && rule.eligibleBandIds.includes(t.bandId))) {
      f.push({ rule: 'transport_not_eligible', outcome: 'exception', params: { transport: code } });
    }
  }
  const km = p.air_preference_km;
  if (km !== undefined && t.distanceKm !== null) {
    const air = t.transports.includes('air');
    if (t.distanceKm >= km && !air && t.transports.length) {
      f.push({ rule: 'ground_over_distance', outcome: 'exception', params: { km } });
    } else if (t.distanceKm < km && air) {
      f.push({ rule: 'air_under_distance', outcome: 'exception', params: { km } });
    }
  }
  if (t.lodgingNightly) {
    const limit = travelLimitFor(policy, {
      kind: 'lodging',
      scope: t.scope,
      country: t.country,
      city: t.city,
      bandId: t.bandId,
    });
    if (limit && limit.currency === t.lodgingNightly.currency) {
      if (toCents(t.lodgingNightly.amount) > toCents(limit.amount)) {
        f.push({
          rule: 'lodging_over_limit',
          outcome: 'exception',
          params: { limit: limit.amount, currency: limit.currency },
        });
      }
    }
  }
  if (t.scope === 'international' && p.passport_min_days !== undefined) {
    const needed = addDays(t.departureOn, p.passport_min_days);
    if (!t.passportValidUntil || t.passportValidUntil < needed) {
      f.push({
        rule: 'passport_validity',
        outcome: 'blocked',
        params: { minDays: p.passport_min_days },
      });
    }
  }
  if (t.restPeriodConflict) f.push({ rule: 'rest_period', outcome: 'exception' });
  return f;
}

/** Adiantamento: antecedência mínima em dias úteis e o teto por dia de viagem. */
export function evaluateAdvance(
  policy: TravelPolicySnapshot,
  t: Pick<TravelTripFacts, 'scope' | 'departureOn' | 'returnOn' | 'country' | 'city' | 'bandId'>,
  a: { requestedOn: string; amount: string; currency: string },
): { findings: TravelFinding[]; max: { amount: string; currency: string } | null } {
  const f: TravelFinding[] = [];
  const minBusiness = policy.parameters.advance_min_business_days;
  if (
    minBusiness !== undefined &&
    businessDaysBetween(a.requestedOn, t.departureOn) < minBusiness
  ) {
    f.push({ rule: 'advance_too_late', outcome: 'blocked', params: { minDays: minBusiness } });
  }
  const limit = travelLimitFor(policy, {
    kind: 'advance',
    scope: t.scope,
    country: t.country,
    city: t.city,
    bandId: t.bandId,
  });
  let max: { amount: string; currency: string } | null = null;
  if (limit) {
    const tripDays = days(t.departureOn, t.returnOn) + 1;
    const total =
      limit.unit === 'per_day' ? toCents(limit.amount) * tripDays : toCents(limit.amount);
    max = { amount: fromCents(total), currency: limit.currency };
    if (limit.currency !== a.currency) {
      f.push({
        rule: 'advance_currency',
        outcome: 'blocked',
        params: { currency: limit.currency },
      });
    } else if (toCents(a.amount) > total) {
      f.push({
        rule: 'advance_over_limit',
        outcome: 'exception',
        params: { limit: max.amount, currency: max.currency },
      });
    }
  }
  return { findings: f, max };
}

export interface TravelExpenseFacts {
  id?: string;
  typeCode: string;
  expenseOn: string;
  meal: Exclude<TravelMeal, 'daily'> | null;
  /** Valor na moeda do limite (o servidor converte e guarda a cotação usada). */
  amount: string;
  currency: string;
  nights: number | null;
  hasReceipt: boolean;
  providedByThirdParty: boolean;
  documentNumber: string | null;
  supplierName: string | null;
  priorAuthorization: boolean;
}

/**
 * Despesa da prestação de contas: proibida nunca é reembolsável; acima do limite, o excedente é
 * exceção (justificativa e alçada); o limite diário da alimentação soma as refeições do dia.
 */
export function evaluateExpense(
  policy: TravelPolicySnapshot,
  trip: Pick<TravelTripFacts, 'scope' | 'departureOn' | 'returnOn' | 'country' | 'city' | 'bandId'>,
  e: TravelExpenseFacts,
  others: readonly TravelExpenseFacts[],
): {
  findings: TravelFinding[];
  limit: { amount: string; currency: string } | null;
  eligibleAmount: string;
  excessAmount: string;
} {
  const f: TravelFinding[] = [];
  const type = policy.expenseTypes.find((x) => x.code === e.typeCode && x.isActive);
  const zero = { limit: null, eligibleAmount: fromCents(0), excessAmount: fromCents(0) };
  if (!type) return { findings: [{ rule: 'expense_type_unknown', outcome: 'blocked' }], ...zero };
  if (type.treatment === 'forbidden') {
    return { findings: [{ rule: 'expense_forbidden', outcome: 'blocked' }], ...zero };
  }
  if (type.requiresReceipt && !e.hasReceipt)
    f.push({ rule: 'receipt_missing', outcome: 'blocked' });
  if (e.providedByThirdParty) f.push({ rule: 'provided_by_third_party', outcome: 'blocked' });
  if (type.requiresPriorAuthorization && !e.priorAuthorization) {
    f.push({ rule: 'prior_authorization_missing', outcome: 'exception' });
  }
  if (type.treatment === 'exception') f.push({ rule: 'expense_exception', outcome: 'exception' });
  if (type.minTripDaysParameter) {
    const min = policy.parameters[type.minTripDaysParameter];
    const tripDays = days(trip.departureOn, trip.returnOn) + 1;
    if (min !== undefined && tripDays <= min) {
      f.push({ rule: 'trip_too_short', outcome: 'blocked', params: { minDays: min } });
    }
  }
  if (e.expenseOn < trip.departureOn || e.expenseOn > trip.returnOn) {
    f.push({ rule: 'outside_trip_period', outcome: 'exception' });
  }
  const dup = others.find(
    (o) =>
      o.id !== e.id &&
      o.typeCode === e.typeCode &&
      o.expenseOn === e.expenseOn &&
      o.amount === e.amount &&
      o.currency === e.currency &&
      (o.documentNumber ?? '') === (e.documentNumber ?? '') &&
      (o.supplierName ?? '').toLowerCase() === (e.supplierName ?? '').toLowerCase(),
  );
  if (dup) f.push({ rule: 'duplicate', outcome: 'blocked' });
  if (f.some((x) => x.outcome === 'blocked')) return { findings: f, ...zero };

  let cap: number | null = null;
  let limitOut: { amount: string; currency: string } | null = null;
  if (type.limitKind && !type.isRepresentation) {
    const base = { scope: trip.scope, country: trip.country, city: trip.city, bandId: trip.bandId };
    const limit =
      type.limitKind === 'meal'
        ? e.meal && travelLimitFor(policy, { ...base, kind: 'meal', meal: e.meal })
        : travelLimitFor(policy, { ...base, kind: type.limitKind });
    if (limit && limit.currency !== e.currency) {
      return {
        findings: [
          ...f,
          { rule: 'currency_mismatch', outcome: 'blocked', params: { currency: limit.currency } },
        ],
        ...zero,
      };
    }
    if (limit) {
      cap = toCents(limit.amount) * (type.limitKind === 'lodging' ? Math.max(1, e.nights ?? 1) : 1);
      limitOut = { amount: fromCents(cap), currency: limit.currency };
    }
    if (type.limitKind === 'meal') {
      const daily = travelLimitFor(policy, { ...base, kind: 'meal', meal: 'daily' });
      if (daily && daily.currency === e.currency) {
        const spent = others
          .filter((o) => o.id !== e.id && o.typeCode === e.typeCode && o.expenseOn === e.expenseOn)
          .reduce((s, o) => s + Math.min(toCents(o.amount), cap ?? toCents(o.amount)), 0);
        const room = Math.max(0, toCents(daily.amount) - spent);
        if (cap === null || room < cap) {
          cap = room;
          limitOut = { amount: fromCents(room), currency: daily.currency };
        }
      }
    }
  }
  const value = toCents(e.amount);
  if (cap !== null && value > cap) {
    f.push({
      rule: 'over_limit',
      outcome: 'exception',
      params: { limit: limitOut!.amount, currency: limitOut!.currency },
    });
    return {
      findings: f,
      limit: limitOut,
      eligibleAmount: fromCents(cap),
      excessAmount: fromCents(value - cap),
    };
  }
  return {
    findings: f,
    limit: limitOut,
    eligibleAmount: fromCents(value),
    excessAmount: fromCents(0),
  };
}

/** Prazo da prestação de contas: dias corridos depois da volta (parâmetro da versão). */
export function accountabilityDueOn(policy: TravelPolicySnapshot, returnOn: string): string | null {
  const n = policy.parameters.accountability_days;
  return n === undefined ? null : addDays(returnOn, n);
}

// ----- Adiantamentos, despesas e prestação de contas (0394) -----

export const TRAVEL_ACCOUNTABILITY_STATUSES = [
  'open',
  'pending_approval',
  'submitted',
  'returned',
  'finished',
] as const;
export const TRAVEL_ADVANCE_FINANCE_STATUSES = [
  'awaiting',
  'released',
  'rejected',
  'settled',
] as const;
export const TRAVEL_FINANCE_STATUSES = ['reimbursed', 'settled', 'rejected'] as const;

const money = z
  .string()
  .trim()
  .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais');
const text = (min: number, max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .default(null)
    .refine((v) => v === null || v.length >= min, `Use pelo menos ${min} caracteres`);

/** Adiantamento pedido para a viagem (o Financeiro libera). */
export const travelAdvanceInput = z.object({
  amount: money,
  currency,
  justification: z.string().trim().min(3).max(1000),
});

/** Despesa da prestação de contas, com o comprovante e a justificativa quando for exceção. */
export const travelExpenseInput = z.object({
  typeCode: z.string().trim().toUpperCase().min(1).max(30),
  meal: z.enum(['breakfast', 'lunch', 'dinner']).nullable().default(null),
  expenseOn: isoDate,
  supplierPartnerId: z.uuid().nullable().default(null),
  supplierName: text(1, 200),
  documentNumber: text(1, 60),
  description: z.string().trim().min(1).max(500),
  amount: money,
  currency,
  nights: z.number().int().min(1).max(365).nullable().default(null),
  costCenterId: z.uuid().nullable().default(null),
  project: text(1, 200),
  hasReceipt: z.boolean().default(false),
  receiptReference: text(1, 200),
  providedByThirdParty: z.boolean().default(false),
  priorAuthorization: z.boolean().default(false),
  participants: text(1, 1000),
  representedCompanies: text(1, 500),
  purpose: text(1, 500),
  justification: text(5, 2000),
});
export type TravelExpenseInput = z.infer<typeof travelExpenseInput>;

/** Devolução da prestação de contas para correção (Facilities), sempre com motivo. */
export const travelAccountabilityReturnInput = z.object({
  reason: z.string().trim().min(5).max(1000),
});

/** Situação do adiantamento informada pelo Financeiro (sem pagamento aqui). */
export const travelAdvanceFinanceInput = z
  .object({
    status: z.enum(TRAVEL_ADVANCE_FINANCE_STATUSES),
    releasedAmount: money.nullable().default(null),
    releasedCurrency: currency.nullable().default(null),
    releasedOn: isoDate.nullable().default(null),
    reference: text(1, 100),
    note: text(1, 1000),
  })
  .refine((v) => !['released', 'settled'].includes(v.status) || !!v.releasedAmount, {
    path: ['releasedAmount'],
    message: 'Informe o valor liberado',
  })
  .refine((v) => !v.releasedAmount || !!v.releasedCurrency, {
    path: ['releasedCurrency'],
    message: 'Informe a moeda',
  });

/** Situação da prestação de contas informada pelo Financeiro (reembolso e acerto do saldo). */
export const travelFinanceInput = z.object({
  status: z.enum(TRAVEL_FINANCE_STATUSES),
  reference: text(1, 100),
  note: text(1, 1000),
  /** Encerra a prestação de contas (nada mais a acertar). */
  finish: z.boolean().default(false),
});
